/*
  DL Auto multi-buy progress bar.

  <dl-multibuy-bar data-mode="product" data-price="1499">  product page (counts the cart + the quantity selected)
  <dl-multibuy-bar data-mode="drawer">                      added to the cart drawer automatically
  <dl-multibuy-bar data-mode="cart">                        cart page

  Tiers and the free-shipping threshold come from snippets/dl-multibuy.liquid.
  Cart numbers come from snippets/dl-multibuy-data.liquid, re-fetched after every cart change.
  The "add a design" suggestions in the cart live in assets/dl-cap-picks.js.
*/
(() => {
  'use strict';
  if (window.DLMultiBuy) return;

  const cfg = window.DLMultiBuyConfig || {};
  const TIERS = (cfg.tiers || []).slice().sort((a, b) => a.min - b.min);
  const SHIP = cfg.shipping || null;
  const GIFT = cfg.gift || null;
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const CART_URL = /\/cart\/(add|change|update|clear)(\.js)?(\?|#|$)/;
  const nativeFetch = window.fetch ? window.fetch.bind(window) : null;
  const CHECK =
    '<svg viewBox="0 0 16 16" focusable="false"><path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  const readData = (scope) => {
    const el = scope && scope.querySelector('script[data-dl-multibuy-data]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  };

  const state = {
    data: readData(document) || { count: 0, original: 0, final: 0, others: 0, country: '', currency: '' },
    lastFill: {},
    lastDone: {},
  };
  const bars = new Set();

  // ---------- helpers ----------
  const fmt = String(cfg.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
  const group = (s, sep) => s.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  const money = (cents) => {
    const value = Math.round(cents) / 100;
    const [int, dec] = value.toFixed(2).split('.');
    const whole = String(Math.round(value));
    const map = {
      amount: `${group(int, ',')}.${dec}`,
      amount_no_decimals: group(whole, ','),
      amount_with_comma_separator: `${group(int, '.')},${dec}`,
      amount_no_decimals_with_comma_separator: group(whole, '.'),
      amount_with_apostrophe_separator: `${group(int, "'")}.${dec}`,
      amount_with_space_separator: `${group(int, ' ')},${dec}`,
      amount_no_decimals_with_space_separator: group(whole, ' '),
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => (key in map ? map[key] : map.amount));
  };
  const pctFor = (qty) => TIERS.reduce((p, t) => (qty >= t.min ? t.pct : p), 0);
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
  const after = (cents, pct) => Math.round((cents * (100 - pct)) / 100);
  const ordinal = (n) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] || 'th'}`;
  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const shipCountry = (d) =>
    !!SHIP && (!SHIP.country || d.country === SHIP.country) && (!SHIP.currency || d.currency === SHIP.currency);

  // ---------- the numbers for one bar ----------
  function compute(bar) {
    const d = state.data;
    let qty = d.count;
    let original = d.original;
    let final = d.final;
    let pending = 0;

    if (bar.mode === 'product') {
      pending = bar.pendingQty();
      qty += pending;
      original += pending * bar.price;
      final = after(original, pctFor(qty));
      // Buy 8, get 1 free: one set at no charge once the 9th is in.
      if (GIFT && qty >= GIFT.buy + 1) final = after(original - bar.price, pctFor(qty));
    }

    const shipOn = d.others === 0 && shipCountry(d);

    const marks = [];
    if (shipOn) {
      (SHIP.steps || []).forEach((s) =>
        marks.push({ kind: 'ship', at: s.qty, cents: s.cents, done: final >= s.cents, title: s.title, reward: s.reward })
      );
    }
    if (GIFT) {
      marks.push({ kind: 'gift', at: GIFT.buy + 1, done: qty >= GIFT.buy + 1, title: GIFT.title || 'Free set', sub: `${GIFT.buy} + 1 free` });
    }
    TIERS.forEach((t) =>
      marks.push({ kind: 'tier', at: t.min, pct: t.pct, bonus: shipOn && t.bonus ? t.bonus : '', done: qty >= t.min, title: `${t.pct}% off` })
    );
    marks.sort((a, b) => a.at - b.at || (a.kind === 'ship' ? -1 : 1));

    // Bar fill: each milestone gets an equal share of the bar.
    const k = marks.length;
    let fill = 0;
    if (k) {
      if (qty >= marks[k - 1].at) {
        fill = 1;
      } else {
        let i = 0;
        let prev = 0;
        while (i < k && qty >= marks[i].at) {
          prev = marks[i].at;
          i += 1;
        }
        let frac = (qty - prev) / (marks[i].at - prev);
        // First stretch: 1 set always reaches halfway to the first checkpoint.
        if (i === 0) frac = qty <= 0 ? 0 : marks[0].at <= 1 ? 1 : 0.5 + (0.5 * (qty - 1)) / (marks[0].at - 1);
        fill = (i + frac) / k;
      }
    }

    const pct = pctFor(qty);
    const next = marks.find((m) => !m.done);
    const lastDone = [...marks].reverse().find((m) => m.done);

    // One set away from free shipping: say what the next set really costs once the postage
    // (shipping.fee) drops off, e.g. a $14.99 set saves $5 postage = $9.99 more.
    // Only while they're still paying postage (no shipping step reached yet); the free express
    // step at 4 sets doesn't save the $5, so it uses the normal "Add 1 more set to ..." line.
    const payingPostage = !marks.some((m) => m.kind === 'ship' && m.done);
    let nextSetFor = 0;
    if (next && next.kind === 'ship' && payingPostage && SHIP && SHIP.fee && qty >= 1 && qty === next.at - 1) {
      const unit = bar.mode === 'product' ? bar.price : d.count ? Math.round(d.original / d.count) : 0;
      const reaches = unit > 0 && after(original + unit, pctFor(qty + 1)) >= next.cents;
      if (reaches && unit > SHIP.fee) nextSetFor = unit - SHIP.fee;
    }

    let msg;
    const anyDesign = bar.mode === 'product' ? ' (any design)' : '';
    if (nextSetFor) {
      msg = `Add a ${ordinal(qty + 1)} set${anyDesign} for just ${money(nextSetFor)} more: shipping becomes free`;
    } else if (!next) {
      const top = TIERS[TIERS.length - 1];
      const extras = `${GIFT ? ' + a free set' : ''}${shipOn ? ' + free express' : ''}`;
      msg = top ? `Best price unlocked: ${top.pct}% off every set${extras}` : '';
    } else if (next.kind === 'gift' && qty >= next.at - 1) {
      msg = `Free set unlocked! Add 1 more set${anyDesign} and it's free`;
    } else {
      const need = Math.max(1, next.kind === 'gift' ? next.at - 1 - qty : next.at - qty);
      let reward = `save ${next.pct}%${next.bonus ? ` + ${next.bonus}` : ''}`;
      if (next.kind === 'ship') reward = next.reward || 'get free shipping';
      if (next.kind === 'gift') reward = 'unlock a free set';
      msg = `${lastDone ? `${lastDone.title} unlocked! ` : ''}Add ${need}${qty > 0 ? ' more' : ''} ${need === 1 ? 'set' : 'sets'}${anyDesign} to ${reward}`;
    }

    const savings = Math.max(0, original - final);
    let foot = '';
    if (bar.mode === 'product') {
      const bits = [];
      if (d.count > 0) bits.push(`Includes ${plural(d.count, 'set')} already in your cart.`);
      if (savings > 0) bits.push(`${plural(qty, 'set')} for ${money(final)}, saving ${money(savings)}.`);
      foot = bits.join(' ');
    } else if (bar.mode === 'page') {
      if (qty > 0) foot = `${plural(qty, 'set')} in your cart${savings > 0 ? `, saving ${money(savings)}` : ''}.`;
    } else if (savings > 0) {
      foot = `You're saving ${money(savings)} on valve caps.`;
    }
    if (SHIP && d.others > 0 && (!SHIP.country || d.country === SHIP.country)) {
      foot = `${foot ? `${foot} ` : ''}Free shipping steps apply to orders with only valve caps.`;
    }

    return { qty, cartCount: d.count, pct, marks, fill, msg, foot, shipOn, nextSetFor };
  }

  // ---------- the element ----------
  class DLMultiBuyBar extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode || 'cart';
      this.price = parseInt(this.dataset.price || '0', 10) || 0;
      this._sig = '';
      bars.add(this);
      this.update();
    }

    disconnectedCallback() {
      bars.delete(this);
    }

    qtyInput() {
      const scope = this.closest('product-info, section, .product') || document;
      return scope.querySelector('input[name="quantity"]');
    }

    pendingQty() {
      const input = this.qtyInput();
      const v = input ? parseInt(input.value, 10) : 1;
      return Number.isFinite(v) && v > 0 ? v : 1;
    }

    update() {
      const v = compute(this);
      const hide = (this.mode === 'drawer' || this.mode === 'cart') && v.cartCount === 0;
      this.hidden = hide;
      if (hide) return;
      const sig = `${this.mode}|${v.marks.map((m) => m.kind + m.at + (m.bonus || '')).join(',')}`;
      if (sig !== this._sig) {
        this.build(v);
        this._sig = sig;
      }
      this.paint(v);
    }

    build(v) {
      const n = v.marks.length;
      const marks = v.marks
        .map(
          (m, i) =>
            `<div class="dlmb__mark" style="left:${((i + 1) / n) * 100}%"><span class="dlmb__dot" aria-hidden="true">${CHECK}</span><span class="dlmb__lbl"><b>${m.title}</b><small>${m.sub || `${plural(m.at, 'set')}${m.bonus ? ` + ${m.bonus}` : ''}`}</small></span></div>`
        )
        .join('');

      const mixUrl = cfg.mixUrl || '';
      let head = '';
      let tiers = '';
      let cta = '';
      if (this.mode === 'product') {
        if (mixUrl) cta = `<a class="dlmb__cta" href="${mixUrl}">Mix &amp; save: pick your designs <span aria-hidden="true">→</span></a>`;
        head = '<div class="dlmb__head"><span class="dlmb__title">Multi-buy</span><span class="dlmb__sub">Mix &amp; match any designs</span></div>';
        const firstMin = TIERS.length ? TIERS[0].min : 2;
        const rows = [{ min: 1, pct: 0, label: firstMin - 1 > 1 ? `1–${firstMin - 1}` : '1' }].concat(
          TIERS.map((t) => ({ min: t.min, pct: t.pct, label: `${t.min}+` }))
        );
        tiers = `<ul class="dlmb__tiers" style="grid-template-columns:repeat(${rows.length},1fr)">${rows
          .map(
            (r) =>
              `<li class="dlmb__tier" data-min="${r.min}"><span class="dlmb__tq">${r.label} sets</span><span class="dlmb__tp">${
                this.price ? money(after(this.price, r.pct)) : r.pct ? `${r.pct}%` : 'Full'
              }</span><span class="dlmb__te">${r.pct ? `save ${r.pct}%` : 'each'}</span></li>`
          )
          .join('')}</ul>`;
      } else if (this.mode !== 'page') {
        const link = mixUrl ? `<a class="dlmb__link" href="${mixUrl}">Add more designs <span aria-hidden="true">→</span></a>` : '';
        head = `<div class="dlmb__head"><span class="dlmb__title">Valve cap multi-buy</span>${link}</div>`;
      }

      const start = state.lastFill[this.mode] || 0;
      this.innerHTML = `<div class="dlmb dlmb--${this.mode}">${head}${tiers}<p class="dlmb__msg" aria-live="polite"></p><div class="dlmb__track${n >= 5 ? ' dlmb__track--dense' : ''}" role="progressbar" aria-valuemin="0" aria-valuemax="100"><span class="dlmb__rail"><span class="dlmb__fill" style="width:${start * 100}%"></span></span>${marks}</div><p class="dlmb__foot"></p>${cta}</div>`;
      this._done = state.lastDone[this.mode] || [];
    }

    paint(v) {
      const msgEl = this.querySelector('.dlmb__msg');
      const footEl = this.querySelector('.dlmb__foot');
      const track = this.querySelector('.dlmb__track');
      const fillEl = this.querySelector('.dlmb__fill');
      if (msgEl) msgEl.textContent = v.msg;
      if (footEl) footEl.textContent = v.foot;
      if (track) {
        track.setAttribute('aria-valuenow', String(Math.round(v.fill * 100)));
        track.setAttribute('aria-valuetext', v.msg);
      }

      const prevDone = this._done || [];
      this.querySelectorAll('.dlmb__mark').forEach((el, i) => {
        const done = !!(v.marks[i] && v.marks[i].done);
        el.classList.toggle('is-done', done);
        if (done && !prevDone[i]) {
          el.classList.remove('is-new');
          void el.offsetWidth;
          el.classList.add('is-new');
        }
      });
      this._done = v.marks.map((m) => m.done);
      state.lastDone[this.mode] = this._done;

      let active = null;
      this.querySelectorAll('.dlmb__tier').forEach((li) => {
        li.classList.remove('is-active');
        if (v.qty >= parseInt(li.dataset.min, 10)) active = li;
      });
      if (active) active.classList.add('is-active');

      if (fillEl) {
        requestAnimationFrame(() => {
          fillEl.style.width = `${v.fill * 100}%`;
        });
      }
      state.lastFill[this.mode] = v.fill;
    }
  }

  const listeners = new Set();
  const updateAll = () => {
    bars.forEach((b) => b.update());
    listeners.forEach((fn) => {
      try {
        fn(state.data);
      } catch (e) {
        /* ignore */
      }
    });
  };

  // ---------- cart changes ----------
  let timer = null;
  const scheduleRefresh = () => {
    clearTimeout(timer);
    timer = setTimeout(refresh, 60);
  };

  async function refresh() {
    if (!nativeFetch) return;
    try {
      const res = await nativeFetch(`${ROOT}?sections=dl-multibuy-data&_dlmb=${Date.now()}`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;
      const json = await res.json();
      const html = json && json['dl-multibuy-data'];
      if (!html) return;
      const d = readData(new DOMParser().parseFromString(html, 'text/html'));
      if (d) {
        state.data = d;
        updateAll();
      }
    } catch (e) {
      /* leave the bar as it was */
    }
  }

  if (nativeFetch) {
    window.fetch = function dlmbFetch(input, init) {
      const p = nativeFetch(input, init);
      try {
        const url = typeof input === 'string' ? input : (input && (input.url || input.href)) || '';
        if (CART_URL.test(String(url))) p.then(scheduleRefresh, () => {});
      } catch (e) {
        /* ignore */
      }
      return p;
    };
  }

  const xhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function dlmbOpen(method, url) {
    try {
      if (CART_URL.test(String(url))) this.addEventListener('loadend', scheduleRefresh);
    } catch (e) {
      /* ignore */
    }
    return xhrOpen.apply(this, arguments);
  };

  window.addEventListener('pageshow', (e) => {
    if (e.persisted) scheduleRefresh();
  });

  // Quantity selector on the product page.
  const onQty = (e) => {
    if (e.target && e.target.matches && e.target.matches('input[name="quantity"]')) {
      bars.forEach((b) => b.mode === 'product' && b.update());
    }
  };
  document.addEventListener('change', onQty);
  document.addEventListener('input', onQty);

  // ---------- cart drawer ----------
  function injectDrawer() {
    document.querySelectorAll('cart-drawer .drawer__inner').forEach((inner) => {
      if (inner.querySelector('dl-multibuy-bar')) return;
      const header = inner.querySelector('.drawer__header');
      if (!header) return;
      const el = document.createElement('dl-multibuy-bar');
      el.setAttribute('data-mode', 'drawer');
      header.insertAdjacentElement('afterend', el);
    });
  }

  function watchDrawer() {
    injectDrawer();
    document.querySelectorAll('cart-drawer').forEach((drawer) => {
      if (drawer.dlmbObserver) return;
      drawer.dlmbObserver = new MutationObserver(injectDrawer);
      drawer.dlmbObserver.observe(drawer, { childList: true, subtree: true });
    });
  }

  customElements.define('dl-multibuy-bar', DLMultiBuyBar);
  window.DLMultiBuy = {
    refresh,
    state,
    money,
    compute,
    cfg,
    tiers: TIERS,
    ship: SHIP,
    ordinal,
    esc,
    shipCountry,
    onUpdate: (fn) => listeners.add(fn),
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchDrawer);
  } else {
    watchDrawer();
  }
})();
